import uniqBy from 'lodash/uniqBy'
import ENUM from '@/enum/parameters'
import { isEntitySelectionType } from '@/enum/entitySelection'
import { WidgetConfig, WidgetGroupList, WidgetParameterValueJson, WidgetTime } from '@/types/dashboard'
import { shouldShowTimeFrame } from '@/helpers/widgetUtils'
import type {
    WidgetParameterBulkUpdateItem,
    WidgetParameterBulkUpdatePayload
} from '@/api/widgetParameterApi'

export const DEFAULT_WIDGET_TIME: WidgetTime = {
    type: null
}

export const DEFAULT_ENTITY_FILTER_VALUE_JSON: WidgetParameterValueJson = {
    AccountList: [],
    EntityNegative: [],
    EntityPositive: [],
    EntityTypeSelection: null
}

/** Unique entity filter stubs for the form (defaults only — not values from widgets). */
export function getUniqEntityFilterStubs (group: WidgetGroupList): WidgetConfig[] {
    const sources = (group.WidgetList || [])
        .flatMap(widget => (widget.WidgetConfig || []) as WidgetConfig[])
        .filter(config =>
            !!config.ParameterName
            && ENUM.AUTO_COMPLETE_TYPE_KEYS.includes(config.ParameterType || -1)
        )

    return uniqBy(sources, 'ParameterName').map(source => ({
        ParameterName: source.ParameterName,
        ParameterType: source.ParameterType,
        ParameterPrettyName: source.ParameterPrettyName,
        ParameterPrettyNameContentTag: source.ParameterPrettyNameContentTag,
        ParameterDescription: source.ParameterDescription,
        ParameterDescriptionTag: source.ParameterDescriptionTag,
        EntityTypeID: source.EntityTypeID,
        WidgetParameterJson: 1,
        WidgetParameterID: 0,
        WidgetParameterValue: '0',
        WidgetParameterValueJson: { ...DEFAULT_ENTITY_FILTER_VALUE_JSON }
    }))
}

export function isBulkFiltersFormDirty (draftConfigs: WidgetConfig[], draftWidgetTime: WidgetTime) {
    return draftConfigs.some(config =>
        isEntitySelectionType(config.WidgetParameterValueJson?.EntityTypeSelection)
    ) || !!draftWidgetTime.type
}

export function buildBulkWidgetFiltersPayload (params: {
    group: WidgetGroupList
    draftConfigs: WidgetConfig[]
    draftWidgetTime: WidgetTime
}): WidgetParameterBulkUpdatePayload {
    const { group, draftConfigs, draftWidgetTime } = params

    const changedConfigs = draftConfigs.filter(config =>
        isEntitySelectionType(config.WidgetParameterValueJson?.EntityTypeSelection)
    )
    const isWidgetTimeChanged = !!draftWidgetTime.type
    const widgets: WidgetParameterBulkUpdateItem[] = []

    for (const widget of group.WidgetList || []) {
        if (!widget.WidgetID) continue

        const widgetConfigUpdates: WidgetConfig[] = []

        for (const draft of changedConfigs) {
            const existing = (widget.WidgetConfig as WidgetConfig[] | undefined)
                ?.find(config => config.ParameterName === draft.ParameterName)

            if (!existing) continue

            widgetConfigUpdates.push({
                ...existing,
                WidgetParameterValueJson: { ...draft.WidgetParameterValueJson },
                WidgetParameterJson: draft.WidgetParameterJson ?? 1,
              })
        }

        const entry: WidgetParameterBulkUpdateItem = {
            WidgetID: widget.WidgetID,
            ...(widgetConfigUpdates.length ? { WidgetConfig: widgetConfigUpdates } : {}),
            ...(isWidgetTimeChanged && shouldShowTimeFrame(widget) ? { WidgetTime: { ...draftWidgetTime } } : {}),
        }

        if (entry.WidgetConfig || entry.WidgetTime) {
            widgets.push(entry)
        }
    }

    return { Widgets: widgets }
}
